import { useEffect, useState, type ReactNode } from 'react';
import { api, type ResumenMes as Datos, type Yo } from '../services/api';
import { mesEnPalabras, sumarMeses } from '../nucleo/fechas';
import { LOGROS } from '../nucleo/logros';
import { PlacaMes } from '../components/PlacaMes';
import { Aviso, BotonVolver, Cargando, Tarjeta, Titulo } from '../components/ui';

/**
 * El resumen de un mes cerrado: arriba "tu mes", abajo el del grupo.
 *
 * El del grupo dice lo mismo que la placa (salen del mismo armado en el
 * servidor); "tu mes" es lo que la placa no puede tener porque es de cada uno.
 */
export function ResumenMes({
  mes,
  yo,
  onVolver,
  onVerPerfil,
}: {
  mes: string;
  yo: Yo;
  onVolver: () => void;
  onVerPerfil: (id: number) => void;
}) {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viendoPlaca, setViendoPlaca] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .resumenMes(mes)
      .then((d) => vivo && setDatos(d))
      .catch((e: unknown) => vivo && setError(e instanceof Error ? e.message : 'Error'));
    return () => {
      vivo = false;
    };
  }, [mes]);

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <BotonVolver onClick={onVolver} />
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (!datos) return <Cargando que="Armando el resumen" />;

  const nombreMes = datos.titulo.split(' ')[0]!;
  const campeones = datos.podio.filter((f) => f.posicion === 1);
  const resto = datos.podio.filter((f) => f.posicion > 1).slice(0, 2);
  const soyCampeon = campeones.some((f) => f.id === yo.jugador.id);

  return (
    <div className="flex flex-col gap-5">
      <BotonVolver onClick={onVolver} />
      <div>
        <p className="font-titulo text-lg text-(--color-acento)">RESUMEN DEL MES</p>
        <Titulo>{datos.titulo}</Titulo>
      </div>

      {!datos.definitivo && (
        <Aviso tono="alerta">
          Provisorio: el mes todavía no cerró y esto puede cambiar. Por ahora lo ves solo vos,
          porque sos admin.
        </Aviso>
      )}

      <TuMes datos={datos} mes={mes} nombreMes={nombreMes} campeon={soyCampeon} />

      <p className="font-titulo text-2xl uppercase">El grupo</p>

      {/* ── El campeón y el podio ── */}
      {campeones.length > 0 && (
        <Tarjeta className="flex flex-col gap-1 border-2 border-(--color-oro)">
          <p className="font-titulo text-xl text-(--color-oro)">
            👑 {campeones.length > 1 ? 'CAMPEONES DEL MES' : 'CAMPEÓN DEL MES'}
          </p>
          <p className="font-titulo text-5xl leading-none">
            {campeones.map((f, i) => (
              <span key={f.id}>
                {i > 0 && ' y '}
                <Nombre id={f.id} yo={yo} onVerPerfil={onVerPerfil}>
                  {f.nombre}
                </Nombre>
              </span>
            ))}
          </p>
          <p className="text-sm">
            <b>
              {campeones[0]!.puntos} puntos · {campeones[0]!.ganados} ganados y {campeones[0]!.perdidos}{' '}
              perdidos
            </b>
            {campeones.length === 1 && (
              <span className="text-(--color-apagado)"> · {conSigno(campeones[0]!.delta_elo)} de nivel</span>
            )}
          </p>
          {textoEl1(datos, campeones) && (
            <p className="mt-1 self-start rounded-lg bg-white px-2.5 py-0.5 font-titulo text-lg text-(--color-fondo)">
              👑 {textoEl1(datos, campeones)}
            </p>
          )}
        </Tarjeta>
      )}
      {resto.length > 0 && (
        <div className="-mt-2 grid grid-cols-2 gap-3">
          {resto.map((f) => (
            <Tarjeta
              key={f.id}
              compacta
              className={`border-2 ${f.posicion === 2 ? 'border-(--color-plata)' : 'border-(--color-bronce)'}`}
            >
              <p
                className={`font-titulo text-lg ${f.posicion === 2 ? 'text-(--color-plata)' : 'text-(--color-bronce)'}`}
              >
                {f.posicion}º
              </p>
              <p className="font-titulo text-3xl leading-none">
                <Nombre id={f.id} yo={yo} onVerPerfil={onVerPerfil}>
                  {f.nombre}
                </Nombre>
              </p>
              <p className="mt-1 text-xs text-(--color-apagado)">
                {f.puntos} pts · {f.ganados}-{f.perdidos} · {conSigno(f.delta_elo)}
              </p>
            </Tarjeta>
          ))}
        </div>
      )}

      {/* ── Reconocimientos ── */}
      {(datos.mas_subio.length > 0 || datos.mas_presente.length > 0) && (
        <Tarjeta compacta className="flex flex-col">
          {datos.mas_subio.length > 0 && (
            <Renglon icono="📈" que="El que más subió" valor={conSigno(datos.mas_subio[0]!.delta_elo)}>
              {datos.mas_subio.map((p) => p.nombre).join(' y ')}
            </Renglon>
          )}
          {datos.mas_presente.length > 0 && (
            <Renglon icono="🙋" que="El más presente" valor={`${datos.mas_presente[0]!.partidos} partidos`}>
              {datos.mas_presente.map((p) => p.nombre).join(' y ')}
            </Renglon>
          )}
        </Tarjeta>
      )}

      {/* ── El mes en números ── */}
      <div className="grid grid-cols-4 gap-2">
        {(
          [
            [datos.numeros.partidos, 'partidos'],
            [datos.numeros.noches, 'noches'],
            [datos.numeros.jugadores, 'jugadores'],
            [datos.numeros.tiebreaks, 'tiebreaks'],
          ] as const
        ).map(([n, que]) => (
          <div key={que} className="rounded-xl bg-white/7 py-2 text-center">
            <p className="font-titulo text-3xl leading-none">{n}</p>
            <p className="text-[11px] text-(--color-apagado)">{que}</p>
          </div>
        ))}
      </div>

      {/* ── Pareja y destacados ── */}
      {(datos.pareja || datos.racha || datos.siempre || datos.mas_peleado.length > 0) && (
        <Tarjeta compacta className="flex flex-col gap-2 text-sm">
          {datos.pareja && (
            <p>
              🤝 <b>Pareja del mes:</b> {datos.pareja.nombres.join(' · ')}{' '}
              <span className="text-(--color-acento)">
                {datos.pareja.ganados}-{datos.pareja.perdidos}
              </span>
            </p>
          )}
          {datos.racha && (
            <p>
              🚀 <b>{datos.racha.nombres.join(' y ')}</b> ganó {datos.racha.largo} seguidos
            </p>
          )}
          {datos.siempre && (
            <p>
              📅 <b>{datos.siempre.nombres.length}</b>{' '}
              {datos.siempre.nombres.length === 1 ? 'vino' : 'vinieron'} las {datos.siempre.noches} noches
              <span className="text-(--color-apagado)"> · {datos.siempre.nombres.map(pegado).join(', ')}</span>
            </p>
          )}
          {datos.mas_peleado.map((p) => (
            <p key={p.fecha + p.ganadores.join()}>
              🎲 <b>{datos.mas_peleado.length > 1 ? 'Los más peleados' : 'El más peleado'}:</b>{' '}
              {p.ganadores.map(pegado).join(' · ')} {p.set} a {p.perdedores.map(pegado).join(' · ')}
              <span className="text-(--color-apagado)">
                {' '}
                ({diaMes(p.fecha)}, tiebreak {p.tiebreak})
              </span>
            </p>
          ))}
        </Tarjeta>
      )}

      {/* ── Logros del mes ── */}
      {datos.logros.length > 0 && (
        <Tarjeta compacta className="flex flex-col gap-1.5 text-sm">
          <p className="font-titulo text-lg text-(--color-acento)">LOGROS DEL MES</p>
          {[...datos.logros]
            .sort((a, b) => b.veces - a.veces)
            .map((l) => {
              const logro = (datos.catalogo_logros ?? LOGROS).find((x) => x.id === l.logro_id);
              if (!logro) return null;
              return (
                <p key={l.logro_id}>
                  {logro.emoji} <b>{logro.nombre}</b>
                  {l.veces > 1 && ` ×${l.veces}`}
                  <span className="text-(--color-apagado)"> · {l.nombres.map(pegado).join(', ')}</span>
                </p>
              );
            })}
        </Tarjeta>
      )}

      {viendoPlaca ? (
        <PlacaMes mes={mes} titulo={datos.titulo} onCerrar={() => setViendoPlaca(false)} />
      ) : (
        <button
          onClick={() => setViendoPlaca(true)}
          className="rounded-xl border border-(--color-acento) bg-(--color-panel) py-2.5 text-center
            font-semibold transition active:scale-[0.98]"
        >
          📸 Placa del mes para el grupo
        </button>
      )}
    </div>
  );
}

/** "Tu mes": cómo te fue a vos, comparado con el mes anterior. */
function TuMes({
  datos,
  mes,
  nombreMes,
  campeon,
}: {
  datos: Datos;
  mes: string;
  nombreMes: string;
  campeon: boolean;
}) {
  const t = datos.tu_mes;
  if (!t.jugaste) {
    return (
      <Tarjeta className="border-2 border-(--color-acento)">
        <p className="font-titulo text-2xl">TU MES</p>
        <p className="mt-1 text-sm text-(--color-apagado)">
          No jugaste en {nombreMes}. El mes que viene arranca de cero para todos.
        </p>
      </Tarjeta>
    );
  }

  const mesAnterior = mesEnPalabras(sumarMeses(mes, -1)).split(' ')[0]!;
  const dif = t.games_favor - t.games_contra;
  // Subir en la tabla es un número más chico: por eso va al revés.
  const cambio =
    t.posicion !== null && t.anterior?.posicion != null ? t.anterior.posicion - t.posicion : null;
  const vecesPorLogro = new Map<string, number>();
  for (const id of t.logros) vecesPorLogro.set(id, (vecesPorLogro.get(id) ?? 0) + 1);

  return (
    <Tarjeta
      className={`flex flex-col gap-3 border-2 ${campeon ? 'border-(--color-oro)' : 'border-(--color-acento)'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-titulo text-2xl">TU MES</p>
          {campeon && <p className="font-semibold text-(--color-oro)">👑 ¡Sos el campeón del mes!</p>}
          {cambio !== null && (
            <p className="text-sm text-(--color-apagado)">
              {cambio > 0
                ? `▲ Subiste ${cambio} ${cambio === 1 ? 'puesto' : 'puestos'} respecto de ${mesAnterior}`
                : cambio < 0
                  ? `▼ Bajaste ${-cambio} ${cambio === -1 ? 'puesto' : 'puestos'} respecto de ${mesAnterior}`
                  : `= El mismo puesto que en ${mesAnterior}`}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          {t.posicion !== null ? (
            <>
              <p className="font-titulo text-5xl leading-none text-(--color-acento)">{t.posicion}º</p>
              <p className="text-xs text-(--color-apagado)">de {t.de}</p>
            </>
          ) : (
            <p className="max-w-32 text-xs text-(--color-apagado)">
              Sin puesto: jugaste pocos partidos para entrar en la corona
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        <Dato valor={String(t.puntos)} que="puntos" />
        <Dato valor={`${t.ganados}-${t.perdidos}`} que="G-P" />
        <Dato valor={dif > 0 ? `+${dif}` : String(dif)} que="games" />
        <Dato valor={conSigno(t.delta_elo)} que="nivel" />
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <p>
          📅 Viniste <b>{t.noches}</b> de {t.noches_del_mes} {t.noches_del_mes === 1 ? 'noche' : 'noches'}
        </p>
        {t.racha >= 2 && (
          <p>
            🔥 Tu mejor racha: <b>{t.racha} ganados seguidos</b>
          </p>
        )}
        {t.companero && (
          <p>
            🤝 Con quién ganaste más: <b>{t.companero.nombres.join(' y ')}</b>{' '}
            <span className="text-(--color-apagado)">({t.companero.ganados} ganados juntos)</span>
          </p>
        )}
      </div>

      {vecesPorLogro.size > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {[...vecesPorLogro].map(([id, veces]) => {
            const logro = (datos.catalogo_logros ?? LOGROS).find((x) => x.id === id);
            if (!logro) return null;
            return (
              <span key={id} className="rounded-full border border-(--color-borde) px-2.5 py-0.5 text-xs">
                {logro.emoji} {logro.nombre}
                {veces > 1 && ` ×${veces}`}
              </span>
            );
          })}
        </div>
      )}
    </Tarjeta>
  );
}

function Dato({ valor, que }: { valor: string; que: string }) {
  return (
    <div className="rounded-xl bg-white/7 py-1.5">
      <p className="font-semibold tabular-nums">{valor}</p>
      <p className="text-[11px] text-(--color-apagado)">{que}</p>
    </div>
  );
}

function Renglon({ icono, que, valor, children }: { icono: string; que: string; valor: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-(--color-borde)/40 py-1.5 last:border-0">
      <span className="w-6 text-center text-lg">{icono}</span>
      <span className="shrink-0 text-xs text-(--color-apagado)">{que}</span>
      <span className="ml-auto min-w-0 text-right font-semibold">
        {children} <span className="text-(--color-acento)">{valor}</span>
      </span>
    </div>
  );
}

/** Un nombre que abre el perfil, con "vos" si sos vos. */
function Nombre({
  id,
  yo,
  onVerPerfil,
  children,
}: {
  id: number;
  yo: Yo;
  onVerPerfil: (id: number) => void;
  children: ReactNode;
}) {
  return (
    <button onClick={() => onVerPerfil(id)} className="text-left underline decoration-(--color-borde) underline-offset-4">
      {children}
      {id === yo.jugador.id && <span className="ml-1.5 font-sans text-xs font-normal text-(--color-apagado)">vos</span>}
    </button>
  );
}

/**
 * Lo que dice la placa sobre “El 1”, en palabras de pantalla. Va adentro de
 * la tarjeta del campeón: si el único campeón es el mismo “El 1”, no se
 * repite el nombre.
 */
function textoEl1(d: Datos, campeones: Datos['podio']): string | null {
  const { nombre, cambio } = d.el_1;
  if (cambio === 'vacante') return '“El 1” quedó vacante: hay empate arriba';
  if (!nombre) return null;
  const quien = campeones.length === 1 && campeones[0]!.nombre === nombre ? '' : `${nombre}: `;
  if (cambio === 'primero') return `${quien}Primer dueño de “El 1”`;
  if (cambio === 'nuevo') return `${quien}Nuevo “El 1”`;
  if (cambio === 'sigue') return `${quien}Sigue siendo “El 1”`;
  return null;
}

/** Un nombre que no se parte entre renglones: "Damian S." no puede quedar "Damian / S.". El reemplazo es un espacio duro (U+00A0). */
function pegado(nombre: string): string {
  return nombre.replace(/ /g, ' ');
}

function conSigno(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '=';
}

/** '2026-09-25' -> '25/9' */
function diaMes(fecha: string): string {
  return `${Number(fecha.slice(8))}/${Number(fecha.slice(5, 7))}`;
}
